import { useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { projects } from "@/data/projects";
import { BrowserFrame } from "@/components/BrowserFrame";
import { Reveal } from "@/components/Reveal";
import { useSectionNav } from "@/lib/scroll";

export default function CaseStudy() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);
  const go = useSectionNav();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  if (!project) return <Navigate to="/work" replace />;

  const chapters = [
    { n: "01", title: "The Challenge", text: project.challenge },
    { n: "02", title: "The Approach", text: project.approach },
    { n: "03", title: "The Result", text: project.result },
  ];

  const meta = [
    ["Client", project.client],
    ["Industry", project.industry],
    ["Services", project.services.join(", ")],
    ["Year", project.year],
  ];

  return (
    <main data-testid="case-study-page" className="pt-32 lg:pt-40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal y={16}>
          <Link
            data-testid="case-study-back-link"
            to="/work"
            className="group inline-flex items-center gap-2 mono text-[11px] tracking-[0.25em] uppercase text-neutral-400 hover:text-neutral-100 transition-colors duration-300"
          >
            <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
            All Work
          </Link>
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500">
            {project.industry} — {project.year}
            {project.concept ? " — Concept Piece" : ""}
          </p>
          <h1 className="font-display font-bold tracking-tight text-neutral-50 text-4xl sm:text-5xl lg:text-6xl mt-5">
            {project.name}
          </h1>
          <p className="text-neutral-400 text-base sm:text-lg mt-4">{project.tagline}</p>
        </Reveal>

        <Reveal delay={0.2} className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-white/5 py-8">
          {meta.map(([k, v]) => (
            <div key={k}>
              <p className="mono text-[10px] tracking-[0.25em] uppercase text-neutral-500">{k}</p>
              <p className="text-sm text-neutral-200 mt-2 leading-relaxed">{v}</p>
            </div>
          ))}
        </Reveal>

        <Reveal delay={0.1} className="mt-16">
          <div ref={heroRef}>
            <BrowserFrame url={project.url}>
              <div className="overflow-hidden">
                <motion.img
                  src={project.images.home}
                  alt={`${project.name} homepage design`}
                  data-testid="case-study-hero-image"
                  style={{ y: imgY, scale: 1.12 }}
                  className="w-full h-auto"
                  draggable="false"
                />
              </div>
            </BrowserFrame>
          </div>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-28 lg:py-36 space-y-20 lg:space-y-28">
        {chapters.map((c) => (
          <Reveal key={c.n} className="grid lg:grid-cols-12 gap-6 lg:gap-8">
            <p className="mono text-sm text-neutral-500 tracking-[0.2em] lg:col-span-2">{c.n}</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-neutral-50 lg:col-span-4">
              {c.title}
            </h2>
            <p className="text-neutral-400 leading-relaxed lg:col-span-6 text-sm sm:text-base">{c.text}</p>
          </Reveal>
        ))}
      </div>

      {project.highlights && (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-28">
          <Reveal>
            <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500">What We Delivered</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-12 mt-8">
            {project.highlights.map((h, i) => (
              <Reveal key={h} delay={i * 0.06}>
                <div className="flex items-start gap-3 border-t border-white/5 py-5 text-sm sm:text-base text-neutral-300">
                  <Check size={16} className="text-neutral-500 mt-0.5 shrink-0" />
                  {h}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-28">
        <Reveal>
          <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500">The Result — In Detail</p>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-end">
          <Reveal className="lg:col-span-8">
            <div data-testid="case-study-gallery-captions" className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mb-6">
              <div data-testid="case-study-desktop-caption" className="border-t border-white/10 pt-4">
                <p className="mono text-[9px] tracking-[0.25em] uppercase text-neutral-500 mb-2">Desktop</p>
                <p className="text-sm text-neutral-400 leading-relaxed">{project.captions.secondary}</p>
              </div>
              <div data-testid="case-study-mobile-caption" className="border-t border-white/10 pt-4">
                <p className="mono text-[9px] tracking-[0.25em] uppercase text-neutral-500 mb-2">Mobile</p>
                <p className="text-sm text-neutral-400 leading-relaxed">{project.captions.mobile}</p>
              </div>
            </div>
            <BrowserFrame url={project.secondaryUrl}>
              <img
                src={project.images.secondary}
                alt={`${project.name} inner page design`}
                data-testid="case-study-secondary-image"
                className="w-full h-auto"
                draggable="false"
              />
            </BrowserFrame>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4 max-w-xs w-full mx-auto lg:mx-0">
            <img
              src={project.images.mobile}
              alt={`${project.name} mobile website design`}
              data-testid="case-study-mobile-image"
              className="w-full h-auto rounded-[2rem]"
              draggable="false"
            />
          </Reveal>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <p className="mono text-[11px] tracking-[0.28em] uppercase text-neutral-500">Next</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-neutral-50 mt-4">
              Your project could be next.
            </h2>
            <p className="text-neutral-400 mt-5 max-w-md leading-relaxed text-sm sm:text-base">
              Tell us about your business and where you want it to go — we'll come back with honest advice, ideas
              and a clear plan for what your new website could look like.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:justify-self-end">
            <button
              data-testid="case-study-cta-btn"
              onClick={() => go("/contact")}
              className="group inline-flex items-center gap-3 rounded-full bg-neutral-50 text-[#0a0a0b] font-medium pl-8 pr-7 py-4 hover:bg-white transition-colors duration-300"
            >
              Start Yours
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
